import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock, PlayCircle, FileText, Headphones, BookOpen, Sparkles, Check } from "lucide-react";
import { v2GetState } from "../../lib/api";

const typeIcon = (t) => {
  const map = {
    VIDEO: PlayCircle, AUDIO: Headphones, TEXT: FileText,
    PDF_WORKBOOK: BookOpen, EXERCISE: Sparkles,
    START_TASK: Sparkles, LIVE_SESSION: PlayCircle,
    SUBMISSION: FileText, ASSESSMENT: FileText,
  };
  return map[t] || FileText;
};

const lockLabel = (reason, unlockAt) => {
  if (reason === "DRIP_PENDING") return unlockAt
    ? `Freigabe am ${new Date(unlockAt).toLocaleDateString("de-CH")}` : "Freigabe nach Zeitplan";
  if (reason === "SCHEDULED_FUTURE") return unlockAt
    ? `Freigabe am ${new Date(unlockAt).toLocaleDateString("de-CH")}` : "Zukunftstermin";
  if (reason === "COHORT_PENDING") return "Kohorten-Freigabe ausstehend";
  if (reason === "MANUAL_LOCKED") return "Freigabe durch SAT ausstehend";
  return "Noch nicht freigegeben";
};

const StatusBadge = ({ status }) => {
  const map = {
    ACTIVE: { c: "text-[#D4AF37] border-[#D4AF37]/40", label: "Zugang aktiv" },
    GRACE: { c: "text-amber-200 border-amber-200/40", label: "Kulanz aktiv" },
    SUSPENDED: { c: "text-white/60 border-white/20", label: "Zugang pausiert" },
    EXPIRED: { c: "text-white/50 border-white/15", label: "Zugang abgelaufen" },
    REVOKED: { c: "text-white/40 border-white/10", label: "Zugang beendet" },
  };
  const meta = map[status] || map.ACTIVE;
  return (
    <span data-testid="entitlement-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-wider uppercase border ${meta.c}`}>
      {meta.label}
    </span>
  );
};

export default function V2CourseState() {
  const { courseSlug } = useParams();
  const [state, setState] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    v2GetState(courseSlug)
      .then(setState)
      .catch((e) => setError(e?.response?.data?.detail || "Kurs nicht erreichbar"));
  }, [courseSlug]);

  if (error) return (
    <div className="pt-32 container-sat" data-testid="course-state-error">
      <Link to="/academy/dashboard" className="btn-ghost text-sm"><ArrowLeft size={14} /> Mein Dashboard</Link>
      <div className="glass mt-6 rounded-2xl p-10 text-center">
        <div className="serif text-2xl mb-2">Zugang derzeit nicht verfügbar</div>
        <p className="text-white/60 text-sm">{error}</p>
      </div>
    </div>
  );
  if (!state) return <div className="pt-32 text-center text-white/55">Lade…</div>;

  return (
    <div className="pt-24 min-h-screen" data-testid={`v2-course-state-${courseSlug}`}>
      <div className="container-sat py-8">
        <Link to="/academy/dashboard" className="btn-ghost text-sm"><ArrowLeft size={14} /> Mein Dashboard</Link>
        <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="overline mb-1">SAT · Kurs</div>
            <h1 className="serif text-4xl md:text-5xl font-light">{state.course.title}</h1>
            {state.course.subtitle && <p className="text-white/60 mt-2">{state.course.subtitle}</p>}
          </div>
          <StatusBadge status={state.entitlement_status} />
        </div>

        <div className="mt-10 grid gap-3" data-testid="v2-lesson-list">
          {state.lessons.map((l, idx) => {
            const Icon = typeIcon(l.lesson_type);
            const unlocked = l.unlocked;
            return unlocked ? (
              <Link
                key={l.id} to={`/academy/${courseSlug}/learn-v2/${l.slug}`}
                data-testid={`v2-lesson-unlocked-${l.position}`}
                className="group glass hover:bg-white/[0.05] rounded-2xl p-5 md:p-6 flex items-center gap-4 transition"
              >
                <div className="shrink-0 w-11 h-11 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline">{l.lesson_type} · Lektion {l.position}</div>
                  <div className="serif text-xl mt-0.5 truncate">{l.title}</div>
                  {l.short_description && (
                    <div className="text-white/55 text-sm mt-1 line-clamp-1">{l.short_description}</div>
                  )}
                </div>
                <div className="text-white/40 text-xs shrink-0">{l.estimated_minutes} min</div>
              </Link>
            ) : (
              <div
                key={l.id}
                data-testid={`v2-lesson-locked-${l.position}`}
                className="rounded-2xl p-5 md:p-6 flex items-center gap-4 border border-white/[0.06] bg-white/[0.02]"
              >
                <div className="shrink-0 w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white/40">
                  <Lock size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline">{l.lesson_type} · Lektion {l.position}</div>
                  <div className="serif text-lg text-white/40">Noch nicht freigegeben</div>
                </div>
                <div className="text-white/40 text-xs shrink-0 max-w-[45%] text-right">
                  {lockLabel(l.lock_reason, l.unlock_at)}
                </div>
              </div>
            );
          })}
          {state.lessons.length === 0 && (
            <div className="glass rounded-2xl p-10 text-center text-white/55">
              Noch keine Lektionen veröffentlicht.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
